import { GoalDocument, TeamAssignment, GoalType, Goal, BehavioralRatings, Signatures } from '../types';
import { Timestamp } from 'firebase/firestore';

// Demo employees for training purposes
export const demoTeamAssignments: TeamAssignment[] = [
  {
    id: 'demo-emp-1',
    employeeEmail: 'demo.employee1@example.com',
    employeeName: 'Alex Johnson',
    employeeTitle: 'Sales Representative',
    managerEmail: 'demo.manager@example.com'
  },
  {
    id: 'demo-emp-2',
    employeeEmail: 'demo.employee2@example.com',
    employeeName: 'Jordan Smith',
    employeeTitle: 'Account Executive',
    managerEmail: 'demo.manager@example.com'
  },
  {
    id: 'demo-emp-3',
    employeeEmail: 'demo.employee3@example.com',
    employeeName: 'Taylor Williams',
    employeeTitle: 'Marketing Coordinator',
    managerEmail: 'demo.manager@example.com'
  },
  {
    id: 'demo-emp-4',
    employeeEmail: 'demo.employee4@example.com',
    employeeName: 'Morgan Davis',
    employeeTitle: 'Project Manager',
    managerEmail: 'demo.manager@example.com'
  },
  {
    id: 'demo-emp-5',
    employeeEmail: 'demo.employee5@example.com',
    employeeName: 'Casey Brown',
    employeeTitle: 'Operations Analyst',
    managerEmail: 'demo.manager@example.com'
  }
];

const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3);
const currentYear = new Date().getFullYear();

const emptySignatures: Signatures = {
  employeeSet: null,
  managerSet: null,
  employeeReview: null,
  managerReview: null
};

const emptyBehavioralRatings: BehavioralRatings = {
  respectForOthers: null,
  integrity: null,
  teamwork: null,
  biasForAction: null,
  carefulCommunication: null,
  growthMindset: null,
  leadByExample: null
};

// Helper to create a Goal object
const makeGoal = (
  num: number,
  type: GoalType,
  title: string,
  description: string,
  employeeRating: 'A' | 'B' | 'NI' | null = null,
  managerRating: 'A' | 'B' | 'NI' | null = null
): Goal => ({
  number: num,
  type,
  title,
  description,
  initialApproval: null,
  employeeRating,
  managerRating
});

// Helper to create demo goals
const makeDemoGoal = (
  id: string,
  userName: string,
  userEmail: string,
  userTitle: string,
  status: GoalDocument['status'],
  goals: Goal[],
  quarter: number = currentQuarter,
  year: number = currentYear,
  signatures: Signatures = { ...emptySignatures },
  behavioralRatings: BehavioralRatings = { ...emptyBehavioralRatings }
): GoalDocument => ({
  id,
  userId: `demo-uid-${id}`,
  userName,
  userEmail,
  userTitle,
  managerEmail: 'demo.manager@example.com',
  managerName: 'Demo Manager',
  quarter,
  year,
  status,
  goals,
  salesGoal: { percentage: null, amount: null },
  performanceGoal: { percentage: null, amount: null },
  behavioralRatings,
  signatures,
  createdAt: Timestamp.fromDate(new Date(year, (quarter - 1) * 3, 15)),
  updatedAt: Timestamp.fromDate(new Date())
});

// Initial demo goals - will be copied to mutable state
const initialDemoGoals: GoalDocument[] = [
  // Submitted - awaiting initial review
  makeDemoGoal(
    'demo-goal-1',
    'Alex Johnson',
    'demo.employee1@example.com',
    'Sales Representative',
    'submitted',
    [
      makeGoal(1, 'normal', 'Increase quarterly sales by 15%', 'Focus on expanding existing client accounts and acquiring new customers in the renewable energy sector.'),
      makeGoal(2, 'normal', 'Complete CRM training certification', 'Finish all modules of the Salesforce certification program by end of quarter.'),
      makeGoal(3, 'normal', 'Maintain 95% customer satisfaction score', 'Regular follow-ups and proactive communication with all assigned accounts.'),
      makeGoal(4, 'stretch', 'Develop 3 new strategic partnerships', 'Identify and establish partnerships with complementary businesses in the solar industry.'),
      makeGoal(5, 'hard', 'Close $500K in new business', 'Target large commercial installations in the northeast region.')
    ]
  ),

  // Submitted - another one awaiting review
  makeDemoGoal(
    'demo-goal-2',
    'Jordan Smith',
    'demo.employee2@example.com',
    'Account Executive',
    'submitted',
    [
      makeGoal(1, 'normal', 'Renew 90% of existing contracts', 'Proactively reach out to clients 60 days before contract expiration.'),
      makeGoal(2, 'normal', 'Reduce response time to under 4 hours', 'Implement email templates and automated responses for common inquiries.'),
      makeGoal(3, 'normal', 'Document all client interactions in CRM', 'Ensure 100% of meetings, calls and emails are logged within 24 hours.'),
      makeGoal(4, 'stretch', 'Achieve "Account Executive of the Quarter"', 'Meet all KPIs and receive positive client feedback.'),
      makeGoal(5, 'hard', 'Expand top 5 accounts by 25%', 'Identify upsell opportunities and present value-added solutions.')
    ]
  ),

  // Approved - quarter in progress
  makeDemoGoal(
    'demo-goal-3',
    'Taylor Williams',
    'demo.employee3@example.com',
    'Marketing Coordinator',
    'approved',
    [
      makeGoal(1, 'normal', 'Launch 4 marketing campaigns', 'Plan and execute campaigns for each month of the quarter.'),
      makeGoal(2, 'normal', 'Increase social media engagement by 20%', 'Create engaging content and maintain consistent posting schedule.'),
      makeGoal(3, 'normal', 'Update all product datasheets', 'Ensure all marketing materials reflect current product specifications.'),
      makeGoal(4, 'stretch', 'Implement marketing automation', 'Set up automated email sequences for lead nurturing.'),
      makeGoal(5, 'hard', 'Generate 500 qualified leads', 'Through combined inbound and outbound marketing efforts.')
    ],
    currentQuarter,
    currentYear,
    {
      employeeSet: { name: 'Taylor Williams', date: Timestamp.fromDate(new Date()) },
      managerSet: { name: 'Demo Manager', date: Timestamp.fromDate(new Date()) },
      employeeReview: null,
      managerReview: null
    }
  ),

  // Final review - employee self-review complete
  makeDemoGoal(
    'demo-goal-4',
    'Morgan Davis',
    'demo.employee4@example.com',
    'Project Manager',
    'final_review',
    [
      makeGoal(1, 'normal', 'Complete 3 project deliverables on time', 'Maintain project timelines and coordinate with cross-functional teams.', 'A'),
      makeGoal(2, 'normal', 'Reduce project costs by 10%', 'Identify efficiencies and negotiate better vendor rates.', 'A'),
      makeGoal(3, 'normal', 'Achieve 100% stakeholder satisfaction', 'Regular status updates and transparent communication.', 'A'),
      makeGoal(4, 'stretch', 'Implement new project management tool', 'Roll out and train team on new PM software.', 'B'),
      makeGoal(5, 'hard', 'Lead company-wide process improvement', 'Identify and implement 3 process improvements across departments.', 'A')
    ],
    currentQuarter,
    currentYear,
    {
      employeeSet: { name: 'Morgan Davis', date: Timestamp.fromDate(new Date()) },
      managerSet: { name: 'Demo Manager', date: Timestamp.fromDate(new Date()) },
      employeeReview: { name: 'Morgan Davis', date: Timestamp.fromDate(new Date()) },
      managerReview: null
    }
  ),

  // Draft - not yet submitted
  makeDemoGoal(
    'demo-goal-5',
    'Casey Brown',
    'demo.employee5@example.com',
    'Operations Analyst',
    'draft',
    [
      makeGoal(1, 'normal', 'Analyze quarterly operations data', 'Provide monthly reports on key operational metrics.'),
      makeGoal(2, 'normal', 'Reduce processing errors by 15%', 'Implement quality checks and standardized procedures.'),
      makeGoal(3, 'normal', 'Complete data visualization training', 'Master Tableau and create interactive dashboards.'),
      makeGoal(4, 'stretch', 'Automate 5 manual processes', 'Identify repetitive tasks and create automation solutions.'),
      makeGoal(5, 'hard', 'Lead database migration project', 'Coordinate migration to new data warehouse.')
    ]
  ),

  // Past quarter - completed
  makeDemoGoal(
    'demo-goal-6',
    'Alex Johnson',
    'demo.employee1@example.com',
    'Sales Representative',
    'completed',
    [
      makeGoal(1, 'normal', 'Q3 Sales Target', 'Achieve quarterly sales quota of $200K.', 'A', 'A'),
      makeGoal(2, 'normal', 'Client Retention', 'Maintain 95% client retention rate.', 'A', 'A'),
      makeGoal(3, 'normal', 'Product Knowledge', 'Complete all product training modules.', 'A', 'A'),
      makeGoal(4, 'stretch', 'Team Mentoring', 'Mentor one new sales representative.', 'B', 'A'),
      makeGoal(5, 'hard', 'Strategic Account Win', 'Close one Fortune 500 account.', 'B', 'B')
    ],
    currentQuarter > 1 ? currentQuarter - 1 : 4,
    currentQuarter > 1 ? currentYear : currentYear - 1,
    {
      employeeSet: { name: 'Alex Johnson', date: Timestamp.fromDate(new Date()) },
      managerSet: { name: 'Demo Manager', date: Timestamp.fromDate(new Date()) },
      employeeReview: { name: 'Alex Johnson', date: Timestamp.fromDate(new Date()) },
      managerReview: { name: 'Demo Manager', date: Timestamp.fromDate(new Date()) }
    }
  )
];

// Mutable demo state - persists during session
let demoGoalsState: GoalDocument[] = JSON.parse(JSON.stringify(initialDemoGoals));

// Reset demo state to initial
export const resetDemoState = () => {
  demoGoalsState = JSON.parse(JSON.stringify(initialDemoGoals));
};

// Export current demo goals (read-only reference)
export const demoGoals = demoGoalsState;

// Get all demo goals
export const getDemoGoals = (): GoalDocument[] => {
  return [...demoGoalsState];
};

// Check if an ID is a demo goal
export const isDemoGoalId = (id: string): boolean => {
  return id.startsWith('demo-goal-');
};

// Get demo goals filtered by status
export const getDemoGoalsByStatus = (statuses: GoalDocument['status'][]): GoalDocument[] => {
  return demoGoalsState.filter(g => statuses.includes(g.status));
};

// Get demo goals for a specific employee
export const getDemoGoalsForEmployee = (email: string): GoalDocument[] => {
  return demoGoalsState.filter(g => g.userEmail === email);
};

// Get a specific demo goal by ID
export const getDemoGoalById = (id: string): GoalDocument | null => {
  return demoGoalsState.find(g => g.id === id) || null;
};

// Update a demo goal's status
export const updateDemoGoalStatus = (id: string, status: GoalDocument['status']): void => {
  const index = demoGoalsState.findIndex(g => g.id === id);
  if (index !== -1) {
    demoGoalsState[index] = {
      ...demoGoalsState[index],
      status,
      updatedAt: Timestamp.fromDate(new Date())
    };
  }
};

// Update demo goal's goals array
export const updateDemoGoalGoals = (id: string, goals: Goal[]): void => {
  const index = demoGoalsState.findIndex(g => g.id === id);
  if (index !== -1) {
    demoGoalsState[index] = {
      ...demoGoalsState[index],
      goals,
      updatedAt: Timestamp.fromDate(new Date())
    };
  }
};

// Complete initial review for a demo goal
export const completeDemoInitialReview = (id: string, managerName: string): void => {
  const index = demoGoalsState.findIndex(g => g.id === id);
  if (index !== -1) {
    demoGoalsState[index] = {
      ...demoGoalsState[index],
      status: 'approved',
      signatures: {
        ...demoGoalsState[index].signatures,
        managerSet: { name: managerName, date: Timestamp.fromDate(new Date()) }
      },
      updatedAt: Timestamp.fromDate(new Date())
    };
  }
};

// Submit employee self-review for a demo goal
export const submitDemoSelfReview = (
  id: string,
  goals: Goal[],
  employeeName: string,
  behavioralRatings?: BehavioralRatings
): void => {
  const index = demoGoalsState.findIndex(g => g.id === id);
  if (index !== -1) {
    demoGoalsState[index] = {
      ...demoGoalsState[index],
      status: 'final_review',
      goals,
      signatures: {
        ...demoGoalsState[index].signatures,
        employeeReview: { name: employeeName, date: Timestamp.fromDate(new Date()) }
      },
      behavioralRatings: behavioralRatings || demoGoalsState[index].behavioralRatings,
      updatedAt: Timestamp.fromDate(new Date())
    };
  }
};

// Complete final review for a demo goal
export const completeDemoFinalReview = (
  id: string,
  goals: Goal[],
  managerName: string,
  behavioralRatings?: BehavioralRatings
): void => {
  const index = demoGoalsState.findIndex(g => g.id === id);
  if (index !== -1) {
    demoGoalsState[index] = {
      ...demoGoalsState[index],
      status: 'completed',
      goals,
      signatures: {
        ...demoGoalsState[index].signatures,
        managerReview: { name: managerName, date: Timestamp.fromDate(new Date()) }
      },
      behavioralRatings: behavioralRatings || demoGoalsState[index].behavioralRatings,
      updatedAt: Timestamp.fromDate(new Date())
    };
  }
};

// Create a new demo goal (for employee creating goals)
export const createNewDemoGoal = (
  userEmail: string,
  userName: string,
  userTitle: string,
  goals: Goal[],
  quarter: number,
  year: number,
  status: 'draft' | 'submitted' = 'draft'
): string => {
  const newId = `demo-goal-${Date.now()}`;
  const newGoal: GoalDocument = {
    id: newId,
    userId: `demo-uid-${newId}`,
    userName,
    userEmail,
    userTitle,
    managerEmail: 'demo.manager@example.com',
    managerName: 'Demo Manager',
    quarter,
    year,
    status,
    goals,
    salesGoal: { percentage: null, amount: null },
    performanceGoal: { percentage: null, amount: null },
    behavioralRatings: { ...emptyBehavioralRatings },
    signatures: { ...emptySignatures },
    createdAt: Timestamp.fromDate(new Date()),
    updatedAt: Timestamp.fromDate(new Date())
  };
  demoGoalsState.push(newGoal);
  return newId;
};

// Update an existing demo goal's content (for drafts)
export const updateDemoGoalDraft = (id: string, goals: Goal[]): void => {
  const index = demoGoalsState.findIndex(g => g.id === id);
  if (index !== -1) {
    demoGoalsState[index] = {
      ...demoGoalsState[index],
      goals,
      updatedAt: Timestamp.fromDate(new Date())
    };
  }
};

// Submit a demo draft goal
export const submitDemoGoal = (id: string): void => {
  const index = demoGoalsState.findIndex(g => g.id === id);
  if (index !== -1 && demoGoalsState[index].status === 'draft') {
    demoGoalsState[index] = {
      ...demoGoalsState[index],
      status: 'submitted',
      updatedAt: Timestamp.fromDate(new Date())
    };
  }
};

// Toggle allowEdit for a demo goal
export const toggleDemoAllowEdit = (id: string, allowEdit: boolean): void => {
  const index = demoGoalsState.findIndex(g => g.id === id);
  if (index !== -1) {
    demoGoalsState[index] = {
      ...demoGoalsState[index],
      allowEdit,
      updatedAt: Timestamp.fromDate(new Date())
    };
  }
};